<template>
  <div id="app">
    <!-- For now, we'll just show GameView directly -->
    <!-- In the future, this would use Vue Router to switch between views -->
    <GameView v-if="currentView === 'game'" :player-data="playerData" @exit-to-lobby="exitToLobby" @view-match-summary="showMatchSummary" />
    <CharacterSelection v-else-if="currentView === 'character-selection'" @start-game="startGame" @go-back="goToMainMenu" />
    <Lobby v-else-if="currentView === 'lobby'" @go-back="goToMainMenu" />
    <MatchSummary v-else-if="currentView === 'match-summary'" :match-data="matchSummaryData" @play-again="currentView = 'character-selection'" @go-to-lobby="currentView = 'lobby'" @exit-to-main-menu="goToMainMenu" />
    <div v-else class="main-menu">
      <h1>Multiplayer Deathmatch Game</h1>
      <p>Welcome to the game! Choose an option to begin.</p>
      
      <!-- Show active game information if exists -->
      <div v-if="activeGameInfo.exists" class="active-game-info">
        <h3>Active Game in Progress</h3>
        <p>Players: {{ activeGameInfo.currentPlayers }} / {{ activeGameInfo.maxPlayers }}</p>
        <p>Spots Left: {{ activeGameInfo.maxPlayers - activeGameInfo.currentPlayers }}</p>
      </div>
      
      <div class="menu-buttons">
        <button @click="currentView = 'character-selection'">Start Here</button>
      </div>
    </div>
  </div>
</template>

<script>
import GameView from './views/GameView.vue';
import CharacterSelection from './views/CharacterSelection.vue';
import Lobby from './views/Lobby.vue';
import MatchSummary from './views/MatchSummary.vue';
import { defineComponent } from 'vue';

// Add this immediately after the imports
let io;
try {
  io = window.io || require('socket.io-client');
} catch (error) {
  console.warn('Socket.io not available, using fallback for active game info');
  io = null;
}

export default defineComponent({
  name: 'App',
  components: {
    GameView,
    CharacterSelection,
    Lobby,
    MatchSummary
  },
  data() {
    return {
      currentView: 'game', // Changed from 'main-menu' to 'game' to load directly into game view
      playerData: null, // Store player data from character selection
      matchSummaryData: null, // Store match summary data
      // New: Active game information
      activeGameInfo: {
        exists: false,
        currentPlayers: 0,
        maxPlayers: 12
      }
    };
  },
  methods: {
    goToMainMenu() {
      this.currentView = 'main-menu';
      console.log('Navigated to main menu');
    },
    startGame(playerData) {
      this.playerData = playerData;
      this.currentView = 'game';
      console.log('App received player data and switched to game view:', playerData);
    },
    exitToLobby() {
      this.currentView = 'character-selection';
      console.log('Exited game to character selection');
    },
    showMatchSummary(matchData) {
      this.matchSummaryData = matchData;
      this.currentView = 'match-summary';
      console.log('Showing match summary with data:', matchData);
    },
    // Method to check for active games (would connect to server)
    checkActiveGames() {
      // If io is not available, use fallback data
      if (!io) {
        this.setFallbackActiveGameData();
        return;
      }
      
      try {
        // Connect to the server via Socket.io to get active game information
        const socket = io('http://localhost:3000');
        
        socket.on('connect', () => {
          console.log('Connected to server to check active games');
          socket.emit('request-active-game-info');
        });
        
        socket.on('active-game-info', (data) => {
          console.log('Received active game info:', data);
          if (data) {
            this.activeGameInfo.exists = data.exists || false;
            this.activeGameInfo.currentPlayers = data.currentPlayers || 0;
            this.activeGameInfo.maxPlayers = data.maxPlayers || 12;
          }
          
          // Disconnect after receiving data
          socket.disconnect();
        });
        
        socket.on('connect_error', (error) => {
          console.error('Failed to connect to server for active game info:', error);
          // Fallback to sample data if connection fails
          this.setFallbackActiveGameData();
        });
        
        socket.on('error', (error) => {
          console.error('Socket error:', error);
          this.setFallbackActiveGameData();
        });
        
        // Set a timeout for the request
        setTimeout(() => {
          if (socket.connected) {
            socket.disconnect();
          }
        }, 5000);
      } catch (error) {
        console.error('Error checking active games:', error);
        this.setFallbackActiveGameData();
      }
    },
    
    // Fallback method to set sample active game data if server connection fails
    setFallbackActiveGameData() {
      this.activeGameInfo.exists = true;
      this.activeGameInfo.currentPlayers = 8;
      this.activeGameInfo.maxPlayers = 12;
    }
  },
  mounted() {
    // Check for active games when component mounts
    this.checkActiveGames();
    
    // Set up periodic check for active games
    setInterval(() => {
      this.checkActiveGames();
    }, 30000); // Check every 30 seconds
  }
});
</script>

<style>
html, body {
  margin: 0;
  padding: 0;
  height: 100%;
  font-family: Avenir, Helvetica, Arial, sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  background-color: #f0f2f5;
}

#app {
  height: 100vh;
  overflow: hidden;
}

.main-menu {
  text-align: center;
  color: #2c3e50;
  margin: 0 auto;
  padding: 50px 30px;
  max-width: 800px;
  background-color: white;
  border-radius: 10px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
}

.main-menu h1 {
  font-size: 36px;
  margin-bottom: 20px;
  color: #333;
}

.main-menu p {
  font-size: 18px;
  margin-bottom: 30px;
  color: #666;
}

/* New style for active game info */
.active-game-info {
  margin: 0 auto 30px;
  padding: 20px;
  background-color: #f8f9fa;
  border-radius: 8px;
  border: 2px solid #4caf50;
  max-width: 400px;
}

.active-game-info h3 {
  margin-top: 0;
  color: #4caf50;
  font-size: 22px;
}

.active-game-info p {
  margin: 10px 0;
  font-size: 16px;
  color: #333;
}

.menu-buttons {
  margin-top: 30px;
  display: flex;
  justify-content: center;
  gap: 20px;
}

button {
  padding: 14px 30px;
  background-color: #4caf50;
  color: white;
  border: none;
  border-radius: 5px;
  font-size: 16px;
  font-weight: bold;
  cursor: pointer;
  transition: all 0.3s;
}

button:hover {
  background-color: #3e8e41;
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
}
</style> 